import {
  cleanNumber,
  formatNumber,
  isValidNumber,
  roundDecimals,
} from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, View } from 'react-native';
import { MaskedNumberInput, PillToggle } from '../../forms';
import { CounterExecutionComponentProps } from './counterExecutionComponent';
import { CounterSummary } from './counterSummary';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export const TallyCounterExecutionComponent = (
  props: CounterExecutionComponentProps
) => {
  const { t } = useTranslation();

  // Initialize state from props on mount
  const getInitialState = () => {
    if (
      props.value !== undefined &&
      props.value !== null &&
      !Number.isNaN(props.value)
    ) {
      const delta = props.value - props.initialValue;
      if (delta !== 0) {
        const absDelta = Math.abs(delta);
        return {
          inputValue: absDelta.toString(),
          addOrRemove: delta > 0 ? 1 : -1,
        };
      }
    }
    return { inputValue: '', addOrRemove: null };
  };

  const initial = getInitialState();
  const [inputValue, setInputValue] = useState<string>(initial.inputValue);
  const [addOrRemove, setAddOrRemove] = useState<number | null>(
    initial.addOrRemove
  );

  const addRemoveOptions = [
    { value: 1, label: t('routines.counter.user.tallyCounter.added') },
    { value: -1, label: t('routines.counter.user.tallyCounter.removed') },
  ];

  useEffect(() => {
    if (inputValue) {
      change(inputValue);
    }
  }, [addOrRemove]);

  const change = (raw: string) => {
    // Ensure raw is a string
    const rawString = raw?.toString() || '';

    if (rawString === '') {
      setInputValue('');
      props.onChange(NaN as any);
      return;
    }
    const input = cleanNumber(rawString, props.decimalPlaces, false);
    setInputValue(input);

    if (isValidNumber(input) && addOrRemove !== null) {
      const inputNumber = parseFloat(input);
      const operationResult = props.initialValue + addOrRemove * inputNumber;
      const roundedResult = roundDecimals(operationResult, props.decimalPlaces);
      props.onChange(roundedResult);
    } else {
      props.onChange(NaN as any);
    }
  };

  const initialValueLabel = t('routines.counter.user.tallyCounter.oldValue');

  const getDeltaValueLabel = () =>
    !addOrRemove
      ? '--'
      : addOrRemove === 1
      ? t('routines.counter.user.tallyCounter.adding')
      : t('routines.counter.user.tallyCounter.removing');

  const getDeltaValue = () => {
    if (!inputValue || !addOrRemove) {
      return '--';
    }

    return addOrRemove === 1
      ? formatNumber(
          parseFloat(inputValue ?? 0),
          props.decimalPlaces ?? 0,
          true
        )
      : `-${formatNumber(
          Math.abs(Number(inputValue)),
          props.decimalPlaces ?? 0
        )}`;
  };

  const getNewValueLabel = () =>
    t('routines.counter.user.tallyCounter.newValue');

  const getNewValue = () => {
    if (
      props.value === undefined ||
      props.value === null ||
      Number.isNaN(props.value)
    )
      return '--';
    return formatNumber(props.value, props.decimalPlaces ?? 0);
  };

  return (
    <View
      style={props.containerStyle ? props.containerStyle : styles.container}
    >
      {props.instructions ? (
        <Text style={styles.sectionTitle}>{props.instructions}</Text>
      ) : null}
      <MaskedNumberInput
        onBlur={props.onBlur}
        innerRef={props.inputRef}
        autoFocus={props.autoFocus}
        onFocus={props.onFocus}
        value={inputValue}
        onChangeText={(val) => change(`${val}`)}
        placeholder={t('routines.counter.user.tallyCounter.placeholder')}
        outlineStyle={styles.inputStyle}
        disableInnerStyle
        decimalPlaces={props.decimalPlaces}
        disabled={props.isIssueEditRoutine || props.disabled}
      />
      <View
        style={[styles.pillToggleContainer, props?.pillToggleContainerStyle]}
      >
        <PillToggle
          options={addRemoveOptions}
          value={addOrRemove}
          setValue={setAddOrRemove}
          disabled={props.isIssueEditRoutine || props.disabled}
        />
      </View>
      <CounterSummary
        initialValueLabel={initialValueLabel}
        initialValue={formatNumber(
          props.initialValue,
          props.decimalPlaces,
          true
        )}
        getDeltaValueLabel={getDeltaValueLabel}
        getDeltaValue={getDeltaValue}
        getNewValueLabel={getNewValueLabel}
        getNewValue={getNewValue}
      />
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    backgroundColor: role.surface,
    borderRadius: 12,
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: role.textPrimary,
    letterSpacing: -0.25,
    marginBottom: 20,
  },
  pillToggleContainer: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  inputStyle: {
    backgroundColor: role.surface,
    borderColor: role.borderStrong,
    borderWidth: 0.5,
    borderRadius: 12,
  },
}));
